<script setup>
import GbDialog from './GbDialog.vue'
import GbButton from './GbButton.vue'
import { computed, ref } from 'vue'

const props = defineProps({
  isBeta: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: 'sm',
    validator: (value) => ['sm', 'md'].includes(value)
  }
})

const emit = defineEmits(['opened'])

const BETA_REMINDER = 'This new feature integrates AI technology and is in active development. Review content to ensure accuracy, relevance, and objectivity.'
const REVIEW_REMINDER = 'Always review content to ensure accuracy, relevance, and objectivity.'

const reviewReminder = computed(() => (props.isBeta ? BETA_REMINDER : REVIEW_REMINDER))

const SIZE_CLASSES = {
  sm: { text: 'text-sm', icon: 'text-lg mr-0.5' },
  md: { text: 'text-md leading-5', icon: 'text-[18.4px] leading-[18.4px] mr-1 mt-[1.6px]' }
}

const showModal = ref(false)

const openModal = () => {
  showModal.value = true
  emit('opened')
}

const closeModal = () => {
  showModal.value = false
}
</script>

<template>
  <div>
    <div class="flex items-start font-regular" :class="SIZE_CLASSES[size].text">
        <span class="material-icons-outlined" :class="SIZE_CLASSES[size].icon">
            info
        </span>
        <div>
          <p v-if="!isBeta">This feature integrates AI technology.</p>
          <p>
            {{ reviewReminder }}
            <button
            class="text-blue-400 hover:underline"
            @click="openModal">
            Learn more
            </button>
          </p>
        </div>
    </div>

    <GbDialog
      dialogId="ai-disclaimer-dialog"
      v-model:visible="showModal"
      header="Goalbook and Responsible AI Use"
      :pt="{ content: { class: ['p-0'] } }"
      :ptOptions="{ mergeSections: true, mergeProps: true }"
      class="ai-disclaimer-dialog"
      :footer-buttons="true"
    >
      <template #bodyContent>
        <div class="text-text-md -m-2">
          <p class="mb-3">
            Goalbook believes that our impact comes from our ability to support what's been shown again and again in research to be the most important school-based factor in student achievement  – the human teacher.
            This belief remains central to our work as we integrate new AI technologies in Goalbook Toolkit.
          </p>
          <p class="mb-3">We are committed to using AI responsibly in our company and our products. Below are some of the ways we are doing that:</p>
          
          <ul class="list-disc pl-5">
            <li class="mb-3">
              <b>Holistic Approach:</b>
              <span>
                Ensuring that our product is high quality, trustworthy, and aligned to research-based practice has guided our content development, software engineering, and product design processes.
                We’re bringing that same commitment to our use of AI technologies.
              </span>
            </li>

            <li class="mb-3">
              <b>Data Security and Privacy:</b>
              <span>
                Our AI technologies are deployed in our private cloud infrastructure so that your data is kept private and secure and regularly SOC 2 evaluated by a third-party auditor.
              </span>
            </li>

            <li class="mb-3">
              <b>Empowered Educators:</b>
              <span>
                Our goal is to integrate AI in ways that center and empower the human educator, rather than replace them.
              </span>
            </li>

            <li class="mb-3">
              <b>Content Safety and Best Practice:</b>
              <span>
                We regularly evaluate and monitor all our content for cultural responsiveness, bias, and inaccuracies, while also ensuring alignment to research-based best practices.
              </span>
            </li>

            <li class="mb-3">
              <b>Community Engagement:</b>
              <span>
                We are committed to continuous learning and improvement and will closely monitor our application of any new technology.
                We seek dialogue, input, and feedback from a diversity of stakeholders so that we can better understand, design, and deliver our products and services.
              </span>
            </li>
          </ul>
        </div>
      </template>
      <template #footerButtonRight>
        <GbButton type="button" gbTheme="bluePrimary" label="OK" @click="closeModal" />
      </template>
    </GbDialog>
  </div>
</template>
